import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Dialog,
  DialogContent,
  useMediaQuery,
  useTheme,
  IconButton,
  Autocomplete,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  addBillingRequestApproval,
  getBillingList,
  getConsolidatedPaymentLink,
  generateConsolidatedPaymentLink,
  cancelConsolidatedPaymentLink,
  checkConsolidatedPaymentStatus,
  sendConsolidatedInvoiceEmail,
} from 'redux/actions/BillingAction';
import CommonButton from 'components/commonButton/CommonButton';
import linkIcon from 'assets/images/Link.svg';
import Loader from 'components/loader/Loader'; // Ensure you import your Loader

function ConfirmAndLinkModel(props) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [cancelLinkConfirmOpen, setCancelLinkConfirmOpen] = useState(false);
  // ── LOCAL STATE FOR PAYMENT LINK ──
  const [localLinkData, setLocalLinkData] = useState(null);

  // ── REDUX STATE FOR PAYMENT LINK ──
  const paymentLinkLoading = useSelector((state) => state?.consolidatedPaymentLink?.loading);

  const handleClose = () => {
    if (props?.billingListpayloadData) {
      dispatch(getBillingList(props?.billingListpayloadData));
    }

    if (typeof props?.onModalClose === 'function') {
      props.onModalClose();
    }

    props?.setSelectAllChecked?.(false);
    props?.setSelectedCheckRows?.([]);
    props.handleClose();
  };

  const linkResponse = useSelector(
    (state) => state.addBillingRequestApprovalLink?.bilingApprovalRequestLink?.data,
  );

  const companyProfile = useSelector(
    (state) => state?.userCompanyDetails?.companyDetails?.Employees,
  );

  useEffect(() => {
    if (props?.selectType !== 'sendConsolidatedInvoice') return;

    if (props?.defaultEmail && isValidEmail(props.defaultEmail)) {
      setEmails([props.defaultEmail]);
      return;
    }

    if (companyProfile) {
      const defaultEmails = companyProfile
        .map((employee) => employee.email)
        .filter((email) => isValidEmail(email));

      setEmails(defaultEmails);
    }
  }, [companyProfile, props?.defaultEmail, props?.selectType]);

  // ── FETCH EXISTING PAYMENT LINK WHEN MODAL OPENS ──
  useEffect(() => {
    if (props.selectType === 'paymentLink' && props.consolidatedInvoiceId) {
      setLocalLinkData(null);
      dispatch(
        getConsolidatedPaymentLink(props.consolidatedInvoiceId, (data) => {
          if (data) {
            let updatedData = { ...data };
            if (updatedData.expiryTime && updatedData.paymentLinkStatus === 'Active') {
              const now = new Date();
              const expiryDate = new Date(updatedData.expiryTime);
              if (now > expiryDate) {
                updatedData.paymentLinkStatus = 'Expired';
              }
            }
            setLocalLinkData(updatedData);
          }
        }),
      );
    }
  }, [props.selectType, props.consolidatedInvoiceId]);

  const handleGmail = () => {
    const paylaod = {
      invoiceIds: props?.totalAmount?.id,
      receipientEmails: emails,
      companyId: props?.billingFilter?.parentCompanyName?.value,
    };
    dispatch(addBillingRequestApproval(paylaod, ''));
    handleClose();
  };

  const isValidEmail = (email) => {
    const trimmedEmail = email.trim();
    const isValid = /\S+@\S+\.\S+/.test(trimmedEmail) && !/\s/.test(trimmedEmail);
    return isValid ? trimmedEmail : false;
  };

  const handleAddEmail = (event, newValue) => {
    const validEmails = newValue.map((email) => isValidEmail(email)).filter(Boolean);
    setEmails((prevEmails) => [...new Set([...prevEmails, ...validEmails])]);
    setInputValue('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmails((prevEmails) => prevEmails.filter((email) => email !== emailToRemove));
  };

  const handleText = () => {
    navigator.clipboard.writeText(linkResponse?.requestUrl);
    toast.success('Copied Successfully');
  };

  // ── PAYMENT LINK ACTION HANDLERS ──
  const handleGenerateLink = () => {
    dispatch(
      generateConsolidatedPaymentLink(props.consolidatedInvoiceId, (data) => {
        if (data) {
          let updatedData = { ...data };
          if (updatedData.expiryTime && updatedData.paymentLinkStatus === 'Active') {
            const now = new Date();
            const expiryDate = new Date(updatedData.expiryTime);
            if (now > expiryDate) {
              updatedData.paymentLinkStatus = 'Expired';
            }
          }
          setLocalLinkData(updatedData);
          // toast.success("Payment link generated successfully");
        }
      }),
    );
  };

  const handleRefreshStatus = () => {
    dispatch(
      checkConsolidatedPaymentStatus(props.consolidatedInvoiceId, (data) => {
        if (data) {
          let updatedData = { ...data };
          if (updatedData.expiryTime && updatedData.paymentLinkStatus === 'Active') {
            const now = new Date();
            const expiryDate = new Date(updatedData.expiryTime);
            if (now > expiryDate) {
              updatedData.paymentLinkStatus = 'Expired';
            }
          }
          setLocalLinkData(updatedData);
          toast.info(`Payment Status: ${updatedData.paymentStatus || 'Pending'}`);
        }
      }),
    );
  };

  const handleCancelLinkClick = () => {
    setCancelLinkConfirmOpen(true);
  };

  const handleConfirmCancelLink = () => {
    dispatch(
      cancelConsolidatedPaymentLink(props.consolidatedInvoiceId, () => {
        setLocalLinkData((prev) => (prev ? { ...prev, paymentLinkStatus: 'Cancelled' } : prev));
        setCancelLinkConfirmOpen(false);
      }),
    );
  };
  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(localLinkData?.paymentLink || '');
    toast.success('Payment link copied');
  };

  const handleSendConsolidatedInvoice = () => {
    dispatch(
      sendConsolidatedInvoiceEmail(props.consolidatedInvoiceId, emails, () => {
        handleClose();
      }),
    );
  };

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open={props.open || false}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
        className="p20"
      >
        <DialogContent className="w500">
          {/* ════════════════════════════════════════════════════
            CASE 1: PAYMENT LINK DISPLAY
            ════════════════════════════════════════════════════ */}
          {props?.selectType === 'paymentLink' && (
            <>
              {/* Header */}
              <Box className="justifyBetween mb20">
                <Typography variant={'head3'} className="fw700 colorPrimary">
                  Payment Status & Options
                </Typography>
                <Box className="pl10">
                  <IconButton aria-label="close" onClick={handleClose} className="modelCloseIcon">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <Typography className="colorSecondary fw600 mb10" variant={'body1'}>
                {props?.companyName || 'NA'}
              </Typography>

              {/* <Typography className="colorGrey mb20" variant={"body2"}>
              Amount: ₹ {Number(props?.totalAmount?.amount || 0).toFixed(2)}
            </Typography> */}

              {/* ── FOR PAID ── */}
              {props?.paymentStatus === 'Paid' && (
                <Box
                  className="mb20 p20"
                  sx={{
                    backgroundColor: '#E8F5E9',
                    borderRadius: '8px',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 700 }}>
                    Paid
                  </Typography>
                  <Typography variant="body2" className="colorGrey">
                    Payment for this invoice is complete.
                  </Typography>
                </Box>
              )}

              {/* ── FOR PARTIALLY PAID ── */}
              {props?.paymentStatus === 'Partially Paid' && (
                <Box
                  className="mb20 p20"
                  sx={{
                    backgroundColor: '#FFEBEE',
                    borderRadius: '8px',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ color: '#C62828', fontWeight: 700 }}>
                    Partially Paid
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#C62828' }}>
                    Outstanding amount is pending. No further action required here.
                  </Typography>
                </Box>
              )}

              {/* ── FOR UNPAID ── */}
              {props?.paymentStatus === 'Unpaid' && (
                <>
                  {paymentLinkLoading && !localLinkData ? (
                    <Box className="mb20" sx={{ textAlign: 'center' }}>
                      <Loader height="5vh" />
                    </Box>
                  ) : localLinkData?.paymentLink &&
                    localLinkData?.paymentLinkStatus === 'Active' ? (
                    /* ── EXISTING ACTIVE LINK UI ── */
                    <>
                      <Grid container item md={12} className="mb20">
                        <TextField
                          className="width100"
                          placeholder="Payment Link"
                          inputProps={{ 'aria-label': 'Payment Link' }}
                          disabled={true}
                          value={localLinkData?.paymentLink || ''}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" onClick={copyLinkToClipboard}>
                                <IconButton>
                                  <img src={linkIcon} alt="copyLink" className="link" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      {/* QR Code
                    {localLinkData?.qrCode && (
                      <Box
                        className="mb20"
                        sx={{ display: "flex", justifyContent: "center" }}
                      >
                        <img
                          src={localLinkData?.qrCode}
                          alt="Payment QR Code"
                          style={{
                            width: 150,
                            height: 150,
                            border: "1px solid #e0e0e0",
                            borderRadius: 8,
                            padding: 8,
                          }}
                        />
                      </Box>
                    )} */}

                      {/* Status Chips */}
                      <Box className="mb10" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip
                          label={`Link Status: ${localLinkData?.paymentLinkStatus || 'NA'}`}
                          color="success"
                          variant="outlined"
                        />
                        {/* <Chip
                        label={`Payment: ${localLinkData?.paymentStatus || "Pending"}`}
                        color="warning"
                        variant="outlined"
                      /> */}
                      </Box>

                      {/* Expiry Time */}
                      {localLinkData?.expiryTime && (
                        <Typography className="colorGrey mb20" variant={'body2'}>
                          Link Valid Until: {new Date(localLinkData?.expiryTime).toLocaleString()}
                        </Typography>
                      )}

                      {/* Action Buttons */}
                      <Box className="flexend mt20" sx={{ display: 'flex', gap: '10px' }}>
                        {/* <CommonButton
                        name={"Copy Link"}
                        btnClass="btnSm"
                        onClick={copyLinkToClipboard}
                      />
                      <CommonButton
                        name={"Refresh Status"}
                        btnClass="btnSm btnOutlined"
                        onClick={handleRefreshStatus}
                      /> */}
                        <CommonButton
                          name={'Regenerate Link'}
                          btnClass="btnSm btnBlue"
                          onClick={handleGenerateLink}
                        />
                        <CommonButton
                          name={'Cancel Link'}
                          btnClass="btnSm btnDanger"
                          onClick={handleCancelLinkClick}
                        />
                      </Box>
                    </>
                  ) : (
                    /* ── NO ACTIVE LINK UI ── */
                    <Box
                      className="mb20 p20"
                      sx={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body1" className="fw600 mb10">
                        No Active Payment Link
                      </Typography>
                      <Typography variant="body2" className="colorGrey mb20">
                        Generate a payment link to share with the client.
                      </Typography>
                      <CommonButton
                        name={'Generate Payment Link'}
                        btnClass="btnSm btnBlue"
                        onClick={handleGenerateLink}
                        disabled={paymentLinkLoading}
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {/* ════════════════════════════════════════════════════
            CASE 2: EXISTING BILL LINK / EMAIL FLOW
            ════════════════════════════════════════════════════ */}
          {props?.selectType === 'billLink' && (
            <>
              {/* Header */}
              <Box className="justifyBetween">
                <Typography variant={'head3'} className="titleStyle fw700 colorPrimary">
                  Share with&nbsp;
                  {props?.companyName?.length <= 15 && (
                    <span className="colorSecondary">{props?.companyName || 'NA'}</span>
                  )}
                </Typography>
                <Box className="pl10">
                  <IconButton aria-label="close" onClick={handleClose} className="modelCloseIcon">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
              <Typography
                className="colorSecondary titleStyle fw700 mb10"
                component={'p'}
                variant={'head3'}
              >
                {props?.companyName?.length > 15 && <span>{props?.companyName || 'NA'}</span>}
              </Typography>

              {/* Link Field */}
              <Grid container item md={12} className="mb10">
                <TextField
                  className="ml10 width100"
                  placeholder="Share Link"
                  inputProps={{ 'aria-label': 'Share Link' }}
                  disabled={true}
                  value={linkResponse?.requestUrl || ''}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" onClick={handleText}>
                        <IconButton>
                          <img src={linkIcon} alt="linkLogo" className="link" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Email Autocomplete */}
              <Autocomplete
                multiple
                disableClearable
                freeSolo
                options={[]}
                value={emails}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                onChange={handleAddEmail}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={index}
                      label={option}
                      {...getTagProps({ index })}
                      onDelete={() => handleRemoveEmail(option)}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Add email"
                    onKeyDown={(event) => {
                      if (event.key === ',') {
                        event.preventDefault();
                        const validEmail = isValidEmail(inputValue);
                        if (validEmail) {
                          setEmails((prevEmails) => [...new Set([...prevEmails, validEmail])]);
                        }
                        setInputValue('');
                      }
                    }}
                  />
                )}
              />

              {/* Send Email Button */}
              <Box className="flexend mt20">
                <CommonButton
                  name={'Send via email'}
                  type="submit"
                  btnClass="btnSm"
                  onClick={handleGmail}
                  // disabled={emails.length === 0}
                />
              </Box>
            </>
          )}

          {/* ════════════════════════════════════════════════════
            CASE 3: SEND CONSOLIDATED INVOICE EMAIL
            ════════════════════════════════════════════════════ */}
          {props?.selectType === 'sendConsolidatedInvoice' && (
            <>
              {/* Header */}
              <Box className="justifyBetween mb20">
                <Typography variant={'head3'} className="titleStyle fw700 colorPrimary">
                  Send Consolidated Invoice
                </Typography>
                <Box className="pl10">
                  <IconButton aria-label="close" onClick={handleClose} className="modelCloseIcon">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
              <Typography
                className="colorSecondary titleStyle fw700 mb10"
                component={'p'}
                variant={'head3'}
              >
                {props?.companyName || 'NA'}
              </Typography>

              <Typography className="colorGrey mb20" variant={'body2'}>
                Outstanding Amount: ₹ {Number(props?.totalAmount?.amount || 0).toFixed(2)}
              </Typography>

              {/* Email Autocomplete */}
              <Autocomplete
                multiple
                disableClearable
                freeSolo
                options={[]}
                value={emails}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                onChange={handleAddEmail}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={index}
                      label={option}
                      {...getTagProps({ index })}
                      onDelete={() => handleRemoveEmail(option)}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Add email"
                    onKeyDown={(event) => {
                      if (event.key === ',') {
                        event.preventDefault();
                        const validEmail = isValidEmail(inputValue);
                        if (validEmail) {
                          setEmails((prevEmails) => [...new Set([...prevEmails, validEmail])]);
                        }
                        setInputValue('');
                      }
                    }}
                  />
                )}
              />

              {/* Send Email Button */}
              <Box className="flexend mt20">
                <CommonButton
                  name={'Send via email'}
                  type="submit"
                  btnClass="btnSm"
                  onClick={handleSendConsolidatedInvoice}
                  // disabled={emails.length === 0}
                />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* ── CANCEL PAYMENT LINK CONFIRMATION ── */}
      <Dialog
        open={cancelLinkConfirmOpen}
        onClose={() => setCancelLinkConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent className="p20">
          <Typography variant="head5" className="fw600 colorBlack mb10">
            Cancel Payment Link
          </Typography>
          <Typography variant="body2" className="colorGrey mb20">
            Are you sure you want to cancel this payment link? This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <CommonButton
              name="Back"
              btnClass="btnSm btnSecondary"
              onClick={() => setCancelLinkConfirmOpen(false)}
            />
            <CommonButton
              name="Confirm Cancel"
              btnClass="btnSm btnDanger"
              onClick={handleConfirmCancelLink}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ConfirmAndLinkModel;
